<template>
    <popup-menu v-if="showLocaleSwitcher">
        <template #default="{ anchorProps }">
            <prime-button
                v-bind="anchorProps"
                type="button"
                text
                severity="secondary"
                class="gap-2"
                :aria-label="selectedLocaleLabel"
            >
                <!-- Show just a globe on smaller screens, else show the selected language -->
                <span
                    v-if="!props.expanded"
                    class="pi pi-globe sm:hidden"
                    aria-hidden="true"
                />
                <span
                    class="font-medium"
                    :class="props.expanded ? '' : 'hidden sm:inline'"
                >
                    {{ selectedLocaleLabel }}
                </span>
                <span
                    class="pi pi-angle-down"
                    aria-hidden="true"
                />
            </prime-button>
        </template>

        <template #content>
            <button
                v-for="localeOption in localeOptions"
                :key="localeOption.value"
                type="button"
                class="flex items-center gap-2 whitespace-nowrap px-6 py-3 text-left text-gray-800 hover:bg-gray-200 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 active:bg-primary-400 active:text-white"
                role="menuitem"
                @click="selectedLocale = localeOption.value"
            >
                {{ localeOption.label }}
            </button>
        </template>
    </popup-menu>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useCookie } from '#app'
import { useI18n } from 'vue-i18n'
import { useLocalization } from '~/composables/use-localization'
import {
    LOCALE_COOKIE_NAME,
    type SupportedLocale
} from '~/composables/create-language-context'
import PrimeButton from 'primevue/button'
import PopupMenu from '~/components/PopupMenu/PopupMenu.vue'

const props = defineProps<{
    // Footer placements can keep the active language visible even at compact breakpoints.
    expanded?: boolean
}>()

const { locale, setLocale } = useI18n({ useScope: 'global' })
const { availableLocales } = useLocalization()

// Keep explicit visitor selections for one year so moving the control does not change persistence behavior.
const localeCookieMaxAgeSeconds = 60 * 60 * 24 * 365
const preferredLocale = useCookie<SupportedLocale | undefined>(
    LOCALE_COOKIE_NAME,
    {
        path: '/',
        sameSite: 'lax',
        maxAge: localeCookieMaxAgeSeconds
    }
)

const localeOptions = computed(() => {
    return availableLocales.value
})

const showLocaleSwitcher = computed(() => {
    // A single-language organization does not need to spend header space on a disabled choice.
    return localeOptions.value.length > 1
})

const selectedLocale = computed<SupportedLocale>({
    get() {
        // Keep the popup selection synchronized with the active storefront locale.
        return locale.value as SupportedLocale
    },
    set(nextLocale) {
        void setPreferredLocale(nextLocale)
    }
})

const selectedLocaleLabel = computed(() => {
    // Render only the current label so short language names do not inherit another option's width.
    return (
        localeOptions.value.find(option => option.value === locale.value)
            ?.label ?? ''
    )
})

async function setPreferredLocale(nextLocale: SupportedLocale) {
    // Write the preference before changing i18n so refreshed locale-aware queries retain the selection.
    preferredLocale.value = nextLocale
    await setLocale(nextLocale)
}
</script>
